/**
 * Per-persona desk memory — each nav-rail persona remembers its last
 * conversation for this browser tab. Manual rail clicks switch desks
 * (restore that persona's session) instead of minting a fresh session on
 * every hop. Contextual navigation (handoff, Open in, back link, canvas
 * drawer) carries the active thread across personas via preserveConversation.
 */


export function deskSessionStorageKey(
  userId: string,
  workspaceId: string,
): string {
  return `crucible_desk_${userId}_${workspaceId}`
}

export function rememberDeskSession(
  userId: string | null | undefined,
  workspaceId: string,
  sessionId: string,
): void {
  if (!userId || typeof sessionStorage === 'undefined') return
  if (!sessionId.startsWith('session_')) return
  sessionStorage.setItem(deskSessionStorageKey(userId, workspaceId), sessionId)
}

export function getDeskSession(
  userId: string | null | undefined,
  workspaceId: string,
): string | null {
  if (!userId || typeof sessionStorage === 'undefined') return null
  const stored = sessionStorage.getItem(deskSessionStorageKey(userId, workspaceId))
  return stored?.startsWith('session_') ? stored : null
}

export function clearDeskSession(
  userId: string | null | undefined,
  workspaceId: string,
): void {
  if (!userId || typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(deskSessionStorageKey(userId, workspaceId))
}

/** True when a manual nav-rail hop should restore/switch desk sessions. */
export function shouldSwitchDeskOnWorkspaceChange(input: {
  prevWorkspaceId: string
  nextWorkspaceId: string
  preserveConversation: boolean
}): boolean {
  if (input.prevWorkspaceId === input.nextWorkspaceId) return false
  if (input.preserveConversation) return false
  return true
}

export type DeskSwitchPlan =
  | { action: 'none' }
  | { action: 'switch'; sessionId: string }
  | { action: 'create' }

/**
 * Decide how to land on a persona after a manual nav-rail click.
 * Remembers the desk being left; restores an existing desk or creates one.
 */
export function planDeskSwitch(input: {
  userId: string | null | undefined
  prevWorkspaceId: string
  nextWorkspaceId: string
  prevSessionId: string
  currentSessionId: string
  preserveConversation: boolean
}): DeskSwitchPlan {
  if (!shouldSwitchDeskOnWorkspaceChange(input)) {
    return { action: 'none' }
  }

  rememberDeskSession(input.userId, input.prevWorkspaceId, input.prevSessionId)

  const deskSession = getDeskSession(input.userId, input.nextWorkspaceId)
  if (deskSession) {
    if (deskSession === input.currentSessionId) return { action: 'none' }
    return { action: 'switch', sessionId: deskSession }
  }

  return { action: 'create' }
}
